<template>
  <section
    v-if="fields && contacts.length"
    :class="rootClass"
    :style="rootStyle"
    :id="rendering?.dataSource"
  >
    <ContentGrid
      :desktop-default-grid="[0, 24]"
      :tablet-default-grid="[0, 16]"
      :mobile-default-grid="[0, 16]"
    >
      <div class="c-regional-contacts__inner">
        <JssRichText :field="fields?.title" tag="h2" />
        <JssText
          v-if="fields.subtitle?.value"
          class="c-regional-contacts__subtitle"
          :field="fields.subtitle"
          tag="p"
        />

        <div class="c-regional-contacts__list">
          <article
            v-for="(contact, index) in contacts"
            :key="contact.id"
            class="c-regional-contacts__item"
            :class="{ 'is-active': activeIndex === index }"
          >
            <button
              class="c-regional-contacts__trigger"
              type="button"
              :aria-expanded="activeIndex === index"
              :aria-controls="`regional-contact-${contact.id}`"
              @click="toggleContact(index)"
            >
              <span class="c-regional-contacts__summary">
                <JssText
                  v-if="contact.fields.region?.value"
                  class="c-regional-contacts__region"
                  :field="contact.fields.region"
                  tag="span"
                />
                <JssText
                  v-if="contact.fields.officeName?.value"
                  class="c-regional-contacts__office"
                  :field="contact.fields.officeName"
                  tag="span"
                />
              </span>
              <span class="c-regional-contacts__chevron" aria-hidden="true"></span>
            </button>

            <div :id="`regional-contact-${contact.id}`" class="c-regional-contacts__panel">
              <p v-if="contact.fields.address?.value" class="c-regional-contacts__detail">
                {{ contact.fields.address.value }}
              </p>
              <p v-if="contact.fields.phone?.value" class="c-regional-contacts__detail">
                {{ contact.fields.phone.value }}
              </p>
              <a
                v-if="contact.fields.email?.value"
                class="c-regional-contacts__email"
                :href="`mailto:${contact.fields.email.value}`"
              >
                {{ contact.fields.email.value }}
              </a>
            </div>
          </article>
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  RegionalContactItemFields,
  RegionalContactsProps,
} from '@/types/components/RegionalContacts';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

interface NormalizedContact extends ScItem<RegionalContactItemFields> {
  fields: RegionalContactItemFields;
}

const props = defineProps<RegionalContactsProps>();
const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);
const activeIndex = ref(0);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-regional-contacts'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const rawContacts = computed<ScItem<RegionalContactItemFields>[]>(() => {
  const contactItems = utils.sc.getFieldObj(fields.value, 'contacts');
  return Array.isArray(contactItems) ? contactItems : [];
});

const contacts = computed<NormalizedContact[]>(() =>
  rawContacts.value.filter((item): item is NormalizedContact => Boolean(item.fields))
);

const toggleContact = (index: number) => {
  activeIndex.value = activeIndex.value === index ? -1 : index;
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-regional-contacts {
  padding: 4px 0 10px;
  background: #ffffff;

  &__inner {
    width: 100%;
  }

  &__title {
    margin: 0;
    color: #333333;
    font-size: 24px;
    line-height: 1.2;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__subtitle {
    margin: 6px 0 12px;
    color: #565656;
    font-size: 12px;
    line-height: 1.45;
    font-weight: 400;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__item {
    overflow: hidden;
    border: 1px solid transparent;
    border-radius: 8px;
    background: rgba(#f3f5f4, 0.6);
    transition:
      border-color 180ms ease,
      background-color 180ms ease;

    &.is-active {
      border-color: #88b091;
      background: #f7faf8;
    }
  }

  &__trigger {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 18px 12px;
    border: 0;
    color: inherit;
    text-align: left;
    background: transparent;
    cursor: pointer;
  }

  &__summary {
    display: block;
    min-width: 0;
  }

  &__region {
    display: block;
    color: #5f6465;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 400;
  }

  &__office {
    display: block;
    margin-top: 6px;
    color: #333333;
    font-size: 16px;
    line-height: 1.2;
    font-weight: 700;
  }

  &__chevron {
    position: relative;
    flex: 0 0 auto;
    width: 12px;
    height: 12px;
    transition: transform 180ms ease;

    &::before {
      content: '';
      position: absolute;
      top: 3px;
      left: 2px;
      width: 7px;
      height: 7px;
      border-right: 1px solid #263a31;
      border-bottom: 1px solid #263a31;
      transform: rotate(45deg);
    }
  }

  &__item.is-active &__chevron {
    transform: rotate(180deg);
  }

  &__panel {
    display: none;
    padding: 0 18px 16px;
  }

  &__item.is-active &__panel {
    display: block;
  }

  &__detail,
  &__email {
    display: block;
    margin: 0;
    color: #54595a;
    font-size: 13px;
    line-height: 1.35;
    font-weight: 400;
    white-space: pre-line;
  }

  &__detail + &__detail,
  &__detail + &__email {
    margin-top: 12px;
  }

  &__email {
    text-decoration: none;

    &:hover,
    &:focus-visible {
      color: #587f5c;
      text-decoration: underline;
    }
  }

  @include tablet {
    padding: 24px 0;

    &__title {
      font-size: 28px;
    }

    &__subtitle {
      margin-bottom: 16px;
      font-size: 14px;
    }
  }

  @include tablet-landscape {
    padding: 40px 0;

    &__title {
      font-size: 32px;
    }

    &__trigger {
      padding: 16px 22px 16px 20px;
    }

    &__panel {
      padding: 0 20px 20px;
    }
  }
}
</style>
